import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import moment from 'moment';
import * as XLSX from 'xlsx';

import DataTable from 'components/shared/DataTable';
import FilterSearchBar from 'components/shared/FilterSearchBar';
import FormDateRange from 'components/shared/BWFormControl/FormDateRange';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import useGetOptions, { optionType } from 'hooks/useGetOptions';
import { getAccountingReport } from 'services/report';
import { defaultParams, showToast } from 'utils/helpers';
import {
  ACCOUNTING_REPORT_PERMISSION,
  getAccountingReportByCode,
  getAccountingReportByPath,
  REPORT_TYPE,
} from './reportConfigs';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import TrialBalanceReport from './TrialBalanceReport';
import { parseAccountingReportSearch } from './accountingReportUrlUtils';
import { createAccountingReportWorkbook, getAccountingReportExcelFileName } from './accountingReportExport';
import { getFinancialSummaryItems } from './financialSummary';
import { getDebtSummaryItems, mapDebtSummaryRows, withDebtSummaryDefaultPeriod } from './debtSummary';
import BankReportFilters, { withBankReportPeriod } from './BankReportFilters';
import './accountingReports.scss';

const currencyFields = new Set([
  'DEBITAMOUNT',
  'CREDITAMOUNT',
  'NETAMOUNT',
  'RUNNINGBALANCE',
  'BALANCEDEBIT',
  'BALANCECREDIT',
  'OPENINGDEBIT',
  'OPENINGCREDIT',
  'CLOSINGDEBIT',
  'CLOSINGCREDIT',
  'OPENINGBALANCE',
  'RECEIVEAMOUNT',
  'PAYMENTAMOUNT',
  'CLOSINGBALANCE',
  'INFLOWAMOUNT',
  'OUTFLOWAMOUNT',
  'INCREASEAMOUNT',
  'DECREASEAMOUNT',
  'BALANCEAMOUNT',
  'TOTALBALANCE',
  'CURRENTAMOUNT',
  'PREVIOUSAMOUNT',
  'OVERDUEAMOUNT',
  'OVERDUE_1_30',
  'OVERDUE_31_60',
  'OVERDUE_61_90',
  'OVERDUE_OVER_90',
  'INPUTVAT',
  'OUTPUTVAT',
  'NETVAT',
  'VATAMOUNT',
  'TAXABLEAMOUNT',
  'ORDERAMOUNT',
  'RECEIVEDAMOUNT',
  'REMAININGAMOUNT',
  'INVOICEAMOUNT',
  'PAIDAMOUNT',
  'CONTRACTVALUE',
  'TOTALORDERAMOUNT',
  'TOTALRECEIVEDAMOUNT',
  'TOTALREMAININGAMOUNT',
  'TOTALINVOICEAMOUNT',
  'TOTALPAIDAMOUNT',
  'TOTALCONTRACTVALUE',
  'TOTALSALESAMOUNT',
  'TRANSFERAMOUNT',
  'TOTALTRANSFERAMOUNT',
  'OUTAMOUNT',
  'INAMOUNT',
  'AMOUNTDIFF',
  'TOTALOUTAMOUNT',
  'TOTALINAMOUNT',
  'TOTALAMOUNTDIFF',
  'UNITPRICE',
  'SALESAMOUNT',
  'DISCOUNTAMOUNT',
  'RETURNAMOUNT',
  'DISCOUNTLINEAMOUNT',
  'COGSUNITPRICE',
  'COGSAMOUNT',
  'GROSSPROFIT',
  'TOTALGROSSPROFIT',
  'POSTINGAMOUNT',
  'RETURNVATAMOUNT',
  'SALESCOGSAMOUNT',
  'RETURNCOGSAMOUNT',
  'CUSTOMERDEDUCTIONAMOUNT',
]);

const dateFields = new Set([
  'ACCOUNTINGDATE',
  'DOCUMENTDATE',
  'FLOWDATE',
  'BALANCEDATE',
  'DUEDATE',
  'REPORTDATE',
  'INVOICEDATE',
  'EXPECTEDDATE',
  'CREATEDDATE',
  'STOCKSINDATE',
  'TRANSFERDATE',
  'FIRSTSTOCKSINDATE',
  'LASTSTOCKSINDATE',
  'STOCKSOUTREQUESTDATE',
]);

const formatDate = (value) => {
  if (!value) return '';
  const parsedDate = moment.utc(value);
  return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : value;
};

const formatReportNumber = (value, maximumFractionDigits = 3) => {
  const numericValue = Number(`${value ?? 0}`.replace(/,/g, ''));
  if (!Number.isFinite(numericValue) || !numericValue) return '0';

  const sign = numericValue < 0 ? '-' : '';
  const absoluteValue = Math.abs(numericValue);
  const [integerPart, decimalPart = ''] = absoluteValue.toFixed(maximumFractionDigits).split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const trimmedDecimal = decimalPart.replace(/0+$/, '');

  return `${sign}${formattedInteger}${trimmedDecimal ? `.${trimmedDecimal}` : ''}`;
};

const formatAmount = (value) => {
  const numericValue = Number(`${value ?? 0}`.replace(/,/g, ''));
  if (!numericValue) return '0';
  const formattedValue = formatReportNumber(Math.abs(numericValue));
  return numericValue < 0 ? <span className='bc_ar_negative'>({formattedValue})</span> : formattedValue;
};

const isEmptyFilterValue = (value) => value === undefined || value === null || value === '';

const normalizeReportParamValue = (value) => {
  if (moment.isMoment(value)) return value.format('YYYY-MM-DD');
  if (value instanceof Date) return moment(value).format('YYYY-MM-DD');
  if (Array.isArray(value)) return value.map(normalizeReportParamValue).filter((item) => !isEmptyFilterValue(item));
  if (value && typeof value === 'object') {
    return value.value ?? value.id ?? value.company_id ?? value.business_id ?? value.store_id ?? value.bank_account_id;
  }
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || null;
  }
  return value;
};

const normalizeAccountingReportParams = (params = {}) =>
  Object.entries(params || {}).reduce((result, [key, value]) => {
    const normalizedValue = normalizeReportParamValue(value);
    result[key] = isEmptyFilterValue(normalizedValue) ? null : normalizedValue;
    return result;
  }, {});

const renderCell = (field) => (row) => {
  const value = row?.[field];
  if (currencyFields.has(field)) {
    if (value === null || value === undefined || value === '') return '';
    return formatAmount(value);
  }
  if (dateFields.has(field)) return formatDate(value);
  return value ?? '';
};

const column = (header, field, options = {}) => ({
  header,
  accessor: field,
  classNameHeader: options.classNameHeader || 'bw_text_center',
  classNameBody: options.classNameBody || (currencyFields.has(field) ? 'bw_text_right' : 'bw_text_left'),
  formatter: renderCell(field),
  ...options,
});

const buildColumns = (reportType, reportCode) => {
  if (reportCode === 'BANK-005') {
    return [column('Số tài khoản ngân hàng', 'BANKNUMBER'), column('Ngân hàng', 'BANKNAME'),
      column('Chi nhánh ngân hàng', 'BANKBRANCH'), column('Chi nhánh hạch toán', 'BUSINESSNAME'),
      column('Tài khoản kế toán', 'ACCOUNTINGACCOUNTCODE'), column('Loại tiền', 'CURRENCYTYPE'),
      column('Số dư đầu kỳ', 'OPENINGBALANCE'), column('Phát sinh Nợ (Thu)', 'RECEIVEAMOUNT'),
      column('Phát sinh Có (Chi)', 'PAYMENTAMOUNT'), column('Số dư cuối kỳ', 'CLOSINGBALANCE')];
  }
  if (reportCode === 'SALE-006') {
    return [
      column('Ngày ghi nhận', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Mã bút toán', 'ACCOUNTINGID', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Số đơn bán', 'ORDERNO'),
      column('Loại bút toán', 'POSTINGTYPENAME'),
      column('Diễn giải', 'EXPLAIN'),
      column('Mã khách hàng', 'CUSTOMERCODE', { classNameBody: 'bw_text_center' }),
      column('Tên khách hàng', 'CUSTOMERNAME'),
      column('Mã hàng', 'PRODUCTCODE', { classNameBody: 'bw_text_center' }),
      column('Tên hàng', 'PRODUCTNAME'),
      column('IMEI/Serial', 'IMEICODE', { classNameBody: 'bw_text_center' }),
      column('ĐVT', 'UNITNAME', { classNameBody: 'bw_text_center' }),
      column('SL bán', 'SELLQUANTITY', { classNameBody: 'bw_text_right' }),
      column('SL trả', 'RETURNQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Đơn giá bán', 'UNITPRICE'),
      column('TK Nợ', 'DEBTACCOUNT', { classNameBody: 'bw_text_center' }),
      column('Tên TK Nợ', 'DEBTACCOUNTNAME'),
      column('TK Có', 'CREDITACCOUNT', { classNameBody: 'bw_text_center' }),
      column('Tên TK Có', 'CREDITACCOUNTNAME'),
      column('Số tiền hạch toán', 'POSTINGAMOUNT'),
      column('Kho', 'STOCKCODE', { classNameBody: 'bw_text_center' }),
      column('Tên kho', 'STOCKNAME'),
      column('Phiếu nhập trả', 'STOCKSINCODE'),
      column('Phiếu xuất bán', 'STOCKSOUTREQUESTCODE'),
      column('Chi nhánh', 'BUSINESSNAME'),
      column('Cửa hàng', 'STORENAME'),
    ];
  }

  if (reportCode === 'SALE-002') {
    return [
      column('Ngày ghi nhận', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Ngày hóa đơn', 'INVOICEDATE', { classNameBody: 'bw_text_center' }),
      column('Số hóa đơn', 'INVOICENO'),
      column('Diễn giải chung', 'EXPLAIN'),
      column('Tên hàng trên chứng từ', 'PRODUCTDISPLAYNAME'),
      column('Mã khách hàng', 'CUSTOMERCODE', { classNameBody: 'bw_text_center' }),
      column('Tên khách hàng', 'CUSTOMERNAME'),
      column('Mã số thuế', 'TAXCODE', { classNameBody: 'bw_text_center' }),
      column('Mã hàng', 'PRODUCTCODE', { classNameBody: 'bw_text_center' }),
      column('Tên hàng', 'PRODUCTNAME'),
      column('ĐVT', 'UNITNAME', { classNameBody: 'bw_text_center' }),
      column('Tổng số lượng bán', 'SELLQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Đơn giá', 'UNITPRICE'),
      column('Doanh số bán', 'SALESAMOUNT'),
      column('TK Nợ', 'DEBTACCOUNT', { classNameBody: 'bw_text_center' }),
      column('TK Có', 'REVENUEACCOUNT', { classNameBody: 'bw_text_center' }),
      column('Chiết khấu', 'DISCOUNTAMOUNT'),
      column('Tổng số lượng trả lại', 'RETURNQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Giá trị trả lại', 'RETURNAMOUNT'),
      column('Giá trị giảm giá', 'DISCOUNTLINEAMOUNT'),
      column('Thuế GTGT', 'VATAMOUNT'),
      column('Tổng thanh toán', 'TOTALPAYMENT'),
      column('Đơn giá vốn', 'COGSUNITPRICE'),
      column('TK giá vốn', 'COGSACCOUNT', { classNameBody: 'bw_text_center' }),
      column('TK kho', 'STOCKACCOUNT', { classNameBody: 'bw_text_center' }),
      column('Giá vốn', 'COGSAMOUNT'),
      column('Mã kho', 'STOCKCODE', { classNameBody: 'bw_text_center' }),
      column('Lãi gộp', 'GROSSPROFIT'),
      column('Tổng lãi gộp', 'TOTALGROSSPROFIT'),
    ];
  }

  if (reportType === REPORT_TYPE.FLOW) {
    return [
      column('Ngày', 'FLOWDATE', { classNameBody: 'bw_text_center' }),
      column('Nguồn chứng từ', 'SOURCETYPE'),
      column('Loại chứng từ', 'DOCUMENTTYPE'),
      column('Dòng tiền vào', 'INFLOWAMOUNT'),
      column('Dòng tiền ra', 'OUTFLOWAMOUNT'),
      column('Dòng tiền thuần', 'NETAMOUNT'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số dòng', 'LINECOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.BALANCE) {
    return [
      column('Ngày', 'BALANCEDATE', { classNameBody: 'bw_text_center' }),
      column('Số dư đầu', 'OPENINGBALANCE'),
      column('Thu', 'RECEIVEAMOUNT'),
      column('Chi', 'PAYMENTAMOUNT'),
      column('Phát sinh thuần', 'NETAMOUNT'),
      column('Số dư cuối', 'CLOSINGBALANCE'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số dòng', 'LINECOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.TRIAL_BALANCE) {
    return [
      column('Mã tài khoản', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Tên tài khoản', 'ACCOUNTINGACCOUNTNAME'),
      column('Dư Nợ đầu kỳ', 'OPENINGDEBIT'),
      column('Dư Có đầu kỳ', 'OPENINGCREDIT'),
      column('Phát sinh Nợ', 'DEBITAMOUNT'),
      column('Phát sinh Có', 'CREDITAMOUNT'),
      column('Dư Nợ cuối kỳ', 'CLOSINGDEBIT'),
      column('Dư Có cuối kỳ', 'CLOSINGCREDIT'),
    ];
  }

  if (reportType === REPORT_TYPE.JOURNAL) {
    return [
      column('Ngày hạch toán', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Loại chứng từ', 'DOCUMENTTYPE'),
      column('Nguồn chứng từ', 'SOURCETYPE'),
      column('Đối tượng', 'OBJECTNAME'),
      column('Diễn giải', 'EXPLAIN'),
      column('TK tiền', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('TK đối ứng', 'RECIPROCALACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Phát sinh Nợ', 'DEBITAMOUNT'),
      column('Phát sinh Có', 'CREDITAMOUNT'),
    ];
  }

  if (reportType === REPORT_TYPE.DEBT_SUMMARY) {
    return [
      column('Mã đối tượng', 'OBJECTCODE', { classNameBody: 'bw_text_center' }),
      column('Đối tượng', 'OBJECTNAME'),
      column('Loại đối tượng', 'OBJECTTYPE', { classNameBody: 'bw_text_center' }),
      column('Tài khoản', 'ACCOUNTINGACCOUNTCODES'),
      column('Dư Nợ đầu kỳ', 'OPENINGDEBIT'),
      column('Dư Có đầu kỳ', 'OPENINGCREDIT'),
      column('Tăng trong kỳ', 'INCREASEAMOUNT'),
      column('Giảm trong kỳ', 'DECREASEAMOUNT'),
      column('Dư Nợ cuối kỳ', 'CLOSINGDEBIT'),
      column('Dư Có cuối kỳ', 'CLOSINGCREDIT'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số dòng', 'LINECOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.DEBT_DETAIL) {
    return [
      column('Ngày hạch toán', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
      column('Hạn thanh toán', 'DUEDATE', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Số hóa đơn', 'INVOICENO'),
      column('Số đơn hàng', 'ORDERNO'),
      column('Đối tượng', 'OBJECTNAME'),
      column('Tài khoản', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('TK đối ứng', 'RECIPROCALACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Diễn giải', 'EXPLAIN'),
      column('Phát sinh tăng', 'INCREASEAMOUNT'),
      column('Phát sinh giảm', 'DECREASEAMOUNT'),
      column('Số dư', 'BALANCEAMOUNT'),
    ];
  }

  if (reportType === REPORT_TYPE.DEBT_AGING) {
    return [
      column('Mã đối tượng', 'OBJECTCODE', { classNameBody: 'bw_text_center' }),
      column('Đối tượng', 'OBJECTNAME'),
      column('Loại đối tượng', 'OBJECTTYPE', { classNameBody: 'bw_text_center' }),
      column('Tài khoản', 'ACCOUNTINGACCOUNTCODES'),
      column('Chưa đến hạn', 'CURRENTAMOUNT'),
      column('Quá hạn', 'OVERDUEAMOUNT'),
      column('1-30 ngày', 'OVERDUE_1_30'),
      column('31-60 ngày', 'OVERDUE_31_60'),
      column('61-90 ngày', 'OVERDUE_61_90'),
      column('Trên 90 ngày', 'OVERDUE_OVER_90'),
      column('Tổng công nợ', 'TOTALBALANCE'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.DEBT_AGING_DETAIL) {
    return [
      column('Ngày hạch toán', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
      column('Hạn thanh toán', 'DUEDATE', { classNameBody: 'bw_text_center' }),
      column('Tuổi nợ', 'AGINGDAYS', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Số hóa đơn', 'INVOICENO'),
      column('Đối tượng', 'OBJECTNAME'),
      column('Tài khoản', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('TK đối ứng', 'RECIPROCALACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Diễn giải', 'EXPLAIN'),
      column('Chưa đến hạn', 'CURRENTAMOUNT'),
      column('Quá hạn', 'OVERDUEAMOUNT'),
      column('Số dư', 'BALANCEAMOUNT'),
    ];
  }

  if (reportType === REPORT_TYPE.TURNOVER_SUMMARY) {
    return [
      column('Ngày', 'REPORTDATE', { classNameBody: 'bw_text_center' }),
      column('Đối tượng', 'OBJECTNAME'),
      column('Loại chứng từ', 'DOCUMENTTYPE'),
      column('Nguồn chứng từ', 'SOURCETYPE'),
      column('Tài khoản', 'ACCOUNTINGACCOUNTCODES'),
      column('Phát sinh Nợ', 'DEBITAMOUNT'),
      column('Phát sinh Có', 'CREDITAMOUNT'),
      column('Giá trị thuần', 'NETAMOUNT'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số dòng', 'LINECOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.TURNOVER_DETAIL || reportType === REPORT_TYPE.VAT_DETAIL) {
    return [
      column(reportCode === 'SALE-005' ? 'Ngày ghi nhận' : 'Ngày hạch toán', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Loại chứng từ', 'DOCUMENTTYPE'),
      column('Nguồn chứng từ', 'SOURCETYPE'),
      column('Đối tượng', 'OBJECTNAME'),
      column('Tài khoản', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Tên tài khoản', 'ACCOUNTINGACCOUNTNAME'),
      column('TK đối ứng', 'RECIPROCALACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Diễn giải', 'EXPLAIN'),
      column('Phát sinh Nợ', 'DEBITAMOUNT'),
      column('Phát sinh Có', 'CREDITAMOUNT'),
      column(reportType === REPORT_TYPE.VAT_DETAIL ? 'Tiền thuế' : 'Giá trị thuần', 'NETAMOUNT'),
    ];
  }

  if (reportType === REPORT_TYPE.PURCHASE_ORDER_PROGRESS) {
    return [
      column('Ngày tạo', 'CREATEDDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày dự kiến', 'EXPECTEDDATE', { classNameBody: 'bw_text_center' }),
      column('Mã đơn mua', 'PURCHASEORDERCODE'),
      column('Nhà cung cấp', 'SUPPLIERNAME'),
      column('Chi nhánh', 'BUSINESSNAME'),
      column('Cửa hàng', 'STORENAME'),
      column('SL đặt', 'ORDERQUANTITY', { classNameBody: 'bw_text_right' }),
      column('SL đã nhận', 'RECEIVEDQUANTITY', { classNameBody: 'bw_text_right' }),
      column('SL còn lại', 'REMAININGQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Giá trị đơn', 'ORDERAMOUNT'),
      column('Giá trị đã nhận', 'RECEIVEDAMOUNT'),
      column('Đã thanh toán', 'PAIDAMOUNT'),
      column('Trạng thái đơn', 'ORDERSTATUSNAME', { classNameBody: 'bw_text_center' }),
      column('Trạng thái thanh toán', 'PAYMENTSTATUSNAME', { classNameBody: 'bw_text_center' }),
      column('Trạng thái hóa đơn', 'INVOICESTATUSNAME', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.PURCHASE_RECEIVING_SUMMARY) {
    return [
      column('Ngày nhập', 'STOCKSINDATE', { classNameBody: 'bw_text_center' }),
      column('Phiếu nhập', 'STOCKSINCODE'),
      column('Mã đơn mua', 'PURCHASEORDERCODE'),
      column('Nhà cung cấp', 'SUPPLIERNAME'),
      column('Mã hàng', 'PRODUCTCODE'),
      column('Tên hàng', 'PRODUCTNAME'),
      column('Đơn vị', 'UNITNAME', { classNameBody: 'bw_text_center' }),
      column('Kho nhập', 'STOCKSNAME'),
      column('Chi nhánh', 'BUSINESSNAME'),
      column('Cửa hàng', 'STORENAME'),
      column('SL nhận', 'RECEIVEDQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Giá trị nhận', 'RECEIVEDAMOUNT'),
    ];
  }

  if (reportType === REPORT_TYPE.PURCHASE_CONTRACT_PROGRESS) {
    return [
      column('Số hợp đồng', 'CONTRACTNO'),
      column('Tên hợp đồng', 'CONTRACTNAME'),
      column('Loại hợp đồng', 'CONTRACTTYPENAME'),
      column('Nhà cung cấp', 'SUPPLIERNAME'),
      column('Chi nhánh', 'BUSINESSNAME'),
      column('Giá trị hợp đồng', 'CONTRACTVALUE'),
      column('Giá trị đơn mua', 'ORDERAMOUNT'),
      column('Giá trị đã nhận', 'RECEIVEDAMOUNT'),
      column('Đã thanh toán', 'PAIDAMOUNT'),
      column('Số đơn mua', 'ORDERCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số phiếu nhập', 'STOCKSINCOUNT', { classNameBody: 'bw_text_center' }),
      column('Hiệu lực từ', 'CONTRACTEFFECTIVEFROM', { classNameBody: 'bw_text_center' }),
      column('Hiệu lực đến', 'CONTRACTEFFECTIVETO', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_SUMMARY) {
    return [
      column('Tài khoản kho', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Tên tài khoản', 'ACCOUNTINGACCOUNTNAME'),
      column('Chi nhánh', 'BUSINESSID', { classNameBody: 'bw_text_center' }),
      column('Cửa hàng', 'STOREID', { classNameBody: 'bw_text_center' }),
      column('Tồn đầu kỳ', 'OPENINGBALANCE'),
      column('Nhập trong kỳ', 'INCREASEAMOUNT'),
      column('Xuất trong kỳ', 'DECREASEAMOUNT'),
      column('Tồn cuối kỳ', 'CLOSINGBALANCE'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số dòng', 'LINECOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_DETAIL) {
    return [
      column('Ngày hạch toán', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
      column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
      column('Số chứng từ', 'DOCUMENTNO'),
      column('Nguồn chứng từ', 'SOURCETYPE'),
      column('Tài khoản kho', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Tên tài khoản', 'ACCOUNTINGACCOUNTNAME'),
      column('TK đối ứng', 'RECIPROCALACCOUNTCODE', { classNameBody: 'bw_text_center' }),
      column('Diễn giải', 'EXPLAIN'),
      column('Nhập', 'INCREASEAMOUNT'),
      column('Xuất', 'DECREASEAMOUNT'),
      column('Tồn', 'BALANCEAMOUNT'),
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_TRANSFER) {
    return [
      column('Ngày chuyển', 'TRANSFERDATE', { classNameBody: 'bw_text_center' }),
      column('Số phiếu chuyển', 'STOCKSTRANSFERCODE'),
      column('Loại chuyển', 'STOCKSTRANSFERTYPENAME'),
      column('Chi nhánh xuất', 'EXPORTBUSINESSNAME'),
      column('Cửa hàng xuất', 'EXPORTSTORENAME'),
      column('Kho xuất', 'EXPORTSTOCKSNAME'),
      column('Chi nhánh nhập', 'IMPORTBUSINESSNAME'),
      column('Cửa hàng nhập', 'IMPORTSTORENAME'),
      column('Kho nhập', 'IMPORTSTOCKSNAME'),
      column('Mã hàng', 'PRODUCTCODE'),
      column('Tên hàng', 'PRODUCTNAME'),
      column('Đơn vị', 'UNITNAME', { classNameBody: 'bw_text_center' }),
      column('SL chuyển', 'TRANSFERQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Giá trị chuyển', 'TRANSFERAMOUNT'),
      column('Trạng thái', 'TRANSFERSTATUSNAME', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_RECONCILIATION) {
    return [
      column('Ngày chuyển', 'TRANSFERDATE', { classNameBody: 'bw_text_center' }),
      column('Phiếu xuất', 'STOCKSOUTREQUESTCODE'),
      column('Phiếu chuyển', 'STOCKSTRANSFERCODE'),
      column('Phiếu nhập', 'STOCKSINCODE'),
      column('Chi nhánh xuất', 'EXPORTBUSINESSNAME'),
      column('Cửa hàng xuất', 'EXPORTSTORENAME'),
      column('Kho xuất', 'EXPORTSTOCKSNAME'),
      column('Chi nhánh nhập', 'IMPORTBUSINESSNAME'),
      column('Cửa hàng nhập', 'IMPORTSTORENAME'),
      column('Kho nhập', 'IMPORTSTOCKSNAME'),
      column('SL xuất', 'OUTQUANTITY', { classNameBody: 'bw_text_right' }),
      column('SL nhập', 'INQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Lệch SL', 'QUANTITYDIFF', { classNameBody: 'bw_text_right' }),
      column('Giá trị xuất', 'OUTAMOUNT'),
      column('Giá trị nhập', 'INAMOUNT'),
      column('Lệch giá trị', 'AMOUNTDIFF'),
      column('Trạng thái', 'RECONCILESTATUSNAME', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_PENDING_TRANSFER) {
    return [
      column('Ngày nhập', 'STOCKSINDATE', { classNameBody: 'bw_text_center' }),
      column('Phiếu nhập', 'STOCKSINCODE'),
      column('Mã yêu cầu', 'REQUESTCODE'),
      column('Kho nguồn', 'FROMSTOCKSNAME'),
      column('Kho nhận', 'STOCKSNAME'),
      column('Chi nhánh', 'BUSINESSNAME'),
      column('Cửa hàng', 'STORENAME'),
      column('Mã hàng', 'PRODUCTCODE'),
      column('Tên hàng', 'PRODUCTNAME'),
      column('Đơn vị', 'UNITNAME', { classNameBody: 'bw_text_center' }),
      column('SL nhập', 'RECEIVEDQUANTITY', { classNameBody: 'bw_text_right' }),
      column('Giá trị nhập', 'RECEIVEDAMOUNT'),
      column('Trạng thái', 'TRANSFERSTATUSNAME', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.VAT_SUMMARY) {
    return [
      column('Kỳ', 'PERIODLABEL', { classNameBody: 'bw_text_center' }),
      column('VAT đầu vào', 'INPUTVAT'),
      column('VAT đầu ra', 'OUTPUTVAT'),
      column('VAT phải nộp/khấu trừ', 'NETVAT'),
      column('Số chứng từ', 'DOCUMENTCOUNT', { classNameBody: 'bw_text_center' }),
      column('Số dòng', 'LINECOUNT', { classNameBody: 'bw_text_center' }),
    ];
  }

  if (reportType === REPORT_TYPE.FINANCIAL_STATEMENT) {
    return [
      column('Mã chỉ tiêu', 'LINECODE', { classNameBody: 'bw_text_center' }),
      column('Chỉ tiêu', 'LINENAME'),
      column('Mã cha', 'PARENTLINECODE', { classNameBody: 'bw_text_center' }),
      column('Thuyết minh', 'NOTE', { classNameBody: 'bw_text_center' }),
      column('Kỳ này', 'CURRENTAMOUNT'),
      column('Kỳ trước', 'PREVIOUSAMOUNT'),
    ];
  }

  const ledgerColumns = [
    column('Ngày hạch toán', 'ACCOUNTINGDATE', { classNameBody: 'bw_text_center' }),
    column('Ngày chứng từ', 'DOCUMENTDATE', { classNameBody: 'bw_text_center' }),
    column('Số chứng từ', 'DOCUMENTNO'),
    column('Mã TK', 'ACCOUNTINGACCOUNTCODE', { classNameBody: 'bw_text_center' }),
    column('Tên tài khoản', 'ACCOUNTINGACCOUNTNAME'),
    column('TK đối ứng', 'RECIPROCALACCOUNTCODE', { classNameBody: 'bw_text_center' }),
    column('Diễn giải', 'EXPLAIN'),
    column('Đối tượng', 'OBJECTNAME'),
    column('Phát sinh Nợ', 'DEBITAMOUNT'),
    column('Phát sinh Có', 'CREDITAMOUNT'),
  ];

  if (reportType === REPORT_TYPE.BOOK) {
    const bankColumns = reportCode === 'BANK-001' ? [column('Số tài khoản ngân hàng', 'BANKNUMBER'),
      column('Ngân hàng', 'BANKNAME'), column('Chi nhánh', 'BUSINESSNAME'), column('Loại tiền', 'CURRENCYTYPE')] : [];
    return [...bankColumns, ...ledgerColumns, column('Số dư', 'RUNNINGBALANCE')];
  }

  return [...ledgerColumns, column('Dư Nợ', 'BALANCEDEBIT'), column('Dư Có', 'BALANCECREDIT')];
};

const getColumns = (reportType, reportCode) => {
  const columns = buildColumns(reportType, reportCode);
  const invoiceReports = [REPORT_TYPE.BOOK, REPORT_TYPE.LEDGER, REPORT_TYPE.JOURNAL, REPORT_TYPE.DEBT_DETAIL,
    REPORT_TYPE.DEBT_AGING_DETAIL, REPORT_TYPE.TURNOVER_DETAIL, REPORT_TYPE.VAT_DETAIL];
  if (reportCode === 'SALE-002' || !invoiceReports.includes(reportType)) return columns;
  const documentIndex = columns.findIndex((item) => item.accessor === 'DOCUMENTNO');
  if (documentIndex >= 0) columns.splice(documentIndex + 1, 0, column('Hóa đơn VAT', 'VATINVOICENO'));
  return columns;
};

const getSummaryItems = (reportType, summary = {}, reportCode) => {
  if (reportCode === 'SALE-006') {
    return [
      ['Doanh thu đã hạch toán', summary.SALESAMOUNT],
      ['Giảm doanh thu trả lại', summary.RETURNAMOUNT],
      ['Thuế GTGT hoàn', summary.RETURNVATAMOUNT],
      ['Giá vốn bán', summary.SALESCOGSAMOUNT],
      ['Giá vốn hoàn', summary.RETURNCOGSAMOUNT],
      ['Số bút toán', summary.ACCOUNTINGCOUNT, false],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
      ['Số dòng hàng', summary.LINECOUNT, false],
    ];
  }

  if (reportCode === 'SALE-002') {
    const items = [
      ['Doanh số bán', summary.TOTALSALESAMOUNT],
      ['Chiết khấu', summary.DISCOUNTAMOUNT],
      ['Giá trị trả lại', summary.RETURNAMOUNT],
      ['Thuế GTGT', summary.VATAMOUNT],
      ['Tổng thanh toán', summary.TOTALPAYMENT],
      ['Giá vốn', summary.COGSAMOUNT],
      ['Lãi gộp', summary.GROSSPROFIT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
      ['Số dòng', summary.LINECOUNT, false],
    ];

    return items.filter(([, value]) => value !== null && value !== undefined && value !== '');
  }

  if (reportType === REPORT_TYPE.FLOW) {
    return [
      ['Dòng tiền vào', summary.INFLOWAMOUNT],
      ['Dòng tiền ra', summary.OUTFLOWAMOUNT],
      ['Dòng tiền thuần', summary.NETAMOUNT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.JOURNAL) {
    return [
      ['Tổng phát sinh Nợ', summary.DEBITAMOUNT],
      ['Tổng phát sinh Có', summary.CREDITAMOUNT],
      ['Chênh lệch', summary.NETAMOUNT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.DEBT_SUMMARY) {
    return getDebtSummaryItems(reportCode, summary);
  }

  if (reportType === REPORT_TYPE.DEBT_DETAIL) {
    return [
      ['Tăng trong kỳ', summary.INCREASEAMOUNT],
      ['Giảm trong kỳ', summary.DECREASEAMOUNT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
      ['Số dòng', summary.LINECOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.DEBT_AGING || reportType === REPORT_TYPE.DEBT_AGING_DETAIL) {
    return [
      ['Chưa đến hạn', summary.CURRENTAMOUNT],
      ['Quá hạn', summary.OVERDUEAMOUNT],
      ['Tổng công nợ', summary.TOTALBALANCE],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
    ];
  }

  if (
    reportType === REPORT_TYPE.TURNOVER_SUMMARY ||
    reportType === REPORT_TYPE.TURNOVER_DETAIL ||
    reportType === REPORT_TYPE.INVENTORY_DETAIL
  ) {
    return [
      ['Phát sinh Nợ', summary.DEBITAMOUNT],
      ['Phát sinh Có', summary.CREDITAMOUNT],
      ['Giá trị thuần', summary.NETAMOUNT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.PURCHASE_ORDER_PROGRESS) {
    return [
      ['Giá trị đơn mua', summary.TOTALORDERAMOUNT],
      ['Giá trị đã nhận', summary.TOTALRECEIVEDAMOUNT],
      ['Giá trị còn lại', summary.TOTALREMAININGAMOUNT],
      ['Đã thanh toán', summary.TOTALPAIDAMOUNT],
      ['Số đơn mua', summary.ORDERCOUNT, false],
      ['Số phiếu nhập', summary.STOCKSINCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.PURCHASE_RECEIVING_SUMMARY) {
    return [
      ['Số lượng nhận', summary.RECEIVEDQUANTITY, false],
      ['Giá trị nhận', summary.TOTALRECEIVEDAMOUNT],
      ['Số đơn mua', summary.ORDERCOUNT, false],
      ['Số phiếu nhập', summary.STOCKSINCOUNT, false],
      ['Số mặt hàng', summary.PRODUCTCOUNT, false],
      ['Số dòng', summary.LINECOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.PURCHASE_CONTRACT_PROGRESS) {
    return [
      ['Giá trị hợp đồng', summary.TOTALCONTRACTVALUE],
      ['Giá trị đơn mua', summary.TOTALORDERAMOUNT],
      ['Giá trị đã nhận', summary.TOTALRECEIVEDAMOUNT],
      ['Đã thanh toán', summary.TOTALPAIDAMOUNT],
      ['Số đơn mua', summary.ORDERCOUNT, false],
      ['Số phiếu nhập', summary.STOCKSINCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_SUMMARY) {
    return [
      ['Tồn đầu kỳ', summary.OPENINGBALANCE],
      ['Nhập trong kỳ', summary.INCREASEAMOUNT],
      ['Xuất trong kỳ', summary.DECREASEAMOUNT],
      ['Tồn cuối kỳ', summary.CLOSINGBALANCE],
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_TRANSFER) {
    return [
      ['Số lượng chuyển', summary.TRANSFERQUANTITY, false],
      ['Giá trị chuyển', summary.TOTALTRANSFERAMOUNT],
      ['Số phiếu chuyển', summary.TRANSFERCOUNT, false],
      ['Số mặt hàng', summary.PRODUCTCOUNT, false],
      ['Số dòng', summary.LINECOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_RECONCILIATION) {
    return [
      ['Số lượng xuất', summary.OUTQUANTITY, false],
      ['Số lượng nhập', summary.INQUANTITY, false],
      ['Lệch số lượng', summary.QUANTITYDIFF, false],
      ['Giá trị xuất', summary.TOTALOUTAMOUNT],
      ['Giá trị nhập', summary.TOTALINAMOUNT],
      ['Lệch giá trị', summary.TOTALAMOUNTDIFF],
    ];
  }

  if (reportType === REPORT_TYPE.INVENTORY_PENDING_TRANSFER) {
    return [
      ['Số lượng nhập', summary.RECEIVEDQUANTITY, false],
      ['Giá trị nhập', summary.TOTALRECEIVEDAMOUNT],
      ['Số phiếu nhập', summary.STOCKSINCOUNT, false],
      ['Số mặt hàng', summary.PRODUCTCOUNT, false],
      ['Số dòng', summary.LINECOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.VAT_SUMMARY) {
    return [
      ['VAT đầu vào', summary.INPUTVAT],
      ['VAT đầu ra', summary.OUTPUTVAT],
      ['VAT phải nộp/khấu trừ', summary.NETVAT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.VAT_DETAIL) {
    return [
      ['Phát sinh Nợ', summary.DEBITAMOUNT],
      ['Phát sinh Có', summary.CREDITAMOUNT],
      ['Tiền thuế', summary.NETAMOUNT],
      ['Số chứng từ', summary.DOCUMENTCOUNT, false],
    ];
  }

  if (reportType === REPORT_TYPE.FINANCIAL_STATEMENT) {
    return getFinancialSummaryItems(reportCode, summary);
  }

  if (reportType === REPORT_TYPE.BALANCE || reportType === REPORT_TYPE.BOOK) {
    return [
      ['Số dư đầu', summary.OPENINGBALANCE],
      ['Thu', summary.RECEIVEAMOUNT],
      ['Chi', summary.PAYMENTAMOUNT],
      ['Số dư cuối', summary.CLOSINGBALANCE],
    ];
  }

  return [
    ['Dư Nợ đầu kỳ', summary.OPENINGDEBIT],
    ['Dư Có đầu kỳ', summary.OPENINGCREDIT],
    ['Phát sinh Nợ', summary.DEBITAMOUNT],
    ['Phát sinh Có', summary.CREDITAMOUNT],
    ['Dư Nợ cuối kỳ', summary.CLOSINGDEBIT],
    ['Dư Có cuối kỳ', summary.CLOSINGCREDIT],
  ];
};

const toPaging = (response = {}, reportCode) => {
  const payload =
    response?.data && !Array.isArray(response.data) && (Array.isArray(response.data.data) || response.data.summary)
      ? response.data
      : response;
  const items = Array.isArray(payload.data) ? payload.data : Array.isArray(payload.items) ? payload.items : [];
  const itemsPerPage = Number(payload.limit || payload.itemsPerPage || defaultParams.itemsPerPage);
  const totalItems = Number(payload.total || payload.totalItems || 0);
  return {
    items: mapDebtSummaryRows(reportCode, items),
    itemsPerPage,
    page: Number(payload.page || 1),
    totalItems,
    totalPages: Math.max(1, Math.ceil(totalItems / itemsPerPage)),
    summary: payload.summary || {},
    meta: payload.meta || {},
  };
};

const toReportFormDate = (value) => {
  if (!value) return null;
  const parsedDate = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
  return parsedDate.isValid() ? parsedDate : null;
};

const GenericAccountingReportPage = ({ report, initialSearch = '' }) => {
  const isBankReport = ['BANK-001', 'BANK-005'].includes(report.code);
  const columns = useMemo(() => getColumns(report.type, report.code), [report.code, report.type]);
  const initialScope = useMemo(() => getPortalScope(), []);
  const initialUrlFilters = useMemo(
    () => withBankReportPeriod(report.code, withDebtSummaryDefaultPeriod(report.code, parseAccountingReportSearch(initialSearch))),
    [initialSearch, report.code],
  );
  const initialFormValues = useMemo(
    () => ({
      company_id: initialUrlFilters.company_id ?? initialScope.company_id ?? 0,
      business_id: initialUrlFilters.business_id ?? initialScope.business_id ?? 0,
      store_id: initialUrlFilters.store_id ?? initialScope.store_id ?? 0,
      accounting_date_from: toReportFormDate(initialUrlFilters.accounting_date_from),
      accounting_date_to: toReportFormDate(initialUrlFilters.accounting_date_to),
      accounting_account_code: initialUrlFilters.accounting_account_code || null,
      document_no: initialUrlFilters.document_no || null,
      keyword: initialUrlFilters.keyword || null,
      bank_account_id: initialUrlFilters.bank_account_id || null,
    }),
    [initialScope, initialUrlFilters],
  );
  const methods = useForm({
    defaultValues: initialFormValues,
  });

  const companyOptions = useGetOptions(optionType.company, { isHasOptionAll: true, checkParams: !isBankReport });
  const businessOptions = useGetOptions(optionType.business, { isHasOptionAll: true, checkParams: !isBankReport });
  const storeOptions = useGetOptions(optionType.store, { isHasOptionAll: true, checkParams: !isBankReport });
  const [params, setParams] = useState(() => ({
    ...defaultParams,
    itemsPerPage: 25,
    ...initialFormValues,
    ...initialUrlFilters,
  }));
  const [dataList, setDataList] = useState({
    items: [],
    itemsPerPage: 25,
    page: 1,
    totalItems: 0,
    totalPages: 1,
    summary: {},
  });
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const requestId = useRef(0);

  const loadData = useCallback(() => {
    const currentRequestId = ++requestId.current;
    setLoading(true);
    getAccountingReport(report.code, normalizeAccountingReportParams(params))
      .then((response) => {
        if (currentRequestId === requestId.current) setDataList(toPaging(response, report.code));
      })
      .catch((error) => {
        if (currentRequestId !== requestId.current) return;
        setDataList(toPaging({}, report.code));
        showToast.error(error?.message || 'Không tải được báo cáo kế toán.');
      })
      .finally(() => {
        if (currentRequestId === requestId.current) setLoading(false);
      });
  }, [params, report.code]);

  useEffect(() => {
    loadData();
    return () => { requestId.current += 1; };
  }, [loadData]);

  useEffect(() => {
    const onScopeChange = (event) => {
      const nextScope = event?.detail || getPortalScope();
      methods.setValue('company_id', nextScope.company_id ?? 0);
      methods.setValue('business_id', nextScope.business_id ?? 0);
      methods.setValue('store_id', nextScope.store_id ?? 0);
      if (isBankReport) {
        methods.setValue('bank_account_id', null);
        methods.setValue('accounting_account_code', null);
      }
      setParams((currentParams) => ({
        ...currentParams,
        ...(isBankReport ? { bank_account_id: null, accounting_account_code: null } : {}),
        company_id: nextScope.company_id ?? 0,
        business_id: nextScope.business_id ?? 0,
        store_id: nextScope.store_id ?? 0,
        page: 1,
      }));
    };

    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, onScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, onScopeChange);
  }, [methods, isBankReport]);

  const onClear = useCallback(() => {
    const scope = getPortalScope();
    const nextValues = withBankReportPeriod(report.code, {
      company_id: scope.company_id ?? 0,
      business_id: scope.business_id ?? 0,
      store_id: scope.store_id ?? 0,
      accounting_date_from: null,
      accounting_date_to: null,
      accounting_account_code: null,
      document_no: null,
      keyword: null,
      bank_account_id: null,
    });
    methods.reset(nextValues);
    setParams({
      ...defaultParams,
      itemsPerPage: 25,
      ...nextValues,
      page: 1,
    });
  }, [methods, report.code]);

  const onSubmitFilter = useCallback((values) => {
    const normalizedValues = normalizeAccountingReportParams(values);
    setParams((currentParams) => ({
      ...currentParams,
      ...normalizedValues,
      keyword: normalizedValues.keyword || null,
      document_no: normalizedValues.document_no || null,
      accounting_account_code: normalizedValues.accounting_account_code || null,
      page: 1,
    }));
  }, []);

  const handleExportExcel = useCallback(() => {
    setLoadingExport(true);
    getAccountingReport(
      report.code,
      normalizeAccountingReportParams({ ...params, page: 1, itemsPerPage: 100000 }),
    )
      .then((response) => {
        const workbook = createAccountingReportWorkbook({
          reportCode: report.code,
          columns,
          rows: toPaging(response, report.code).items,
          amountFields: currencyFields,
          dateFields,
        });
        XLSX.writeFile(workbook, getAccountingReportExcelFileName(report.code));
      })
      .catch((error) => showToast.error(error?.message || 'Không xuất được Excel.'))
      .finally(() => setLoadingExport(false));
  }, [columns, params, report.code]);

  const viewPermissions = useMemo(() => {
    if (report.viewPermission) return report.viewPermission;
    if (report.code === 'GL-005') return ['AC_TRIAL_BALANCE_VIEW', ACCOUNTING_REPORT_PERMISSION];
    if (report.code === 'GL-001') return ['AC_ACCOUNT_LEDGER_VIEW', ACCOUNTING_REPORT_PERMISSION];
    if (report.code.startsWith('FIN-')) return ['AC_FINANCIAL_REPORT_VIEW', ACCOUNTING_REPORT_PERMISSION];
    return ACCOUNTING_REPORT_PERMISSION;
  }, [report.code, report.viewPermission]);
  const exportPermissions = useMemo(() => {
    if (report.viewPermission) return report.viewPermission;
    if (report.code === 'GL-005') return ['AC_TRIAL_BALANCE_EXPORT', ACCOUNTING_REPORT_PERMISSION];
    if (report.code === 'GL-001') return ['AC_ACCOUNT_LEDGER_EXPORT', ACCOUNTING_REPORT_PERMISSION];
    if (report.code.startsWith('FIN-')) return ['AC_FINANCIAL_REPORT_EXPORT', ACCOUNTING_REPORT_PERMISSION];
    return ACCOUNTING_REPORT_PERMISSION;
  }, [report.code, report.viewPermission]);

  const actions = useMemo(
    () => [
      {
        globalAction: true,
        icon: 'fi fi-rr-refresh',
        color: 'blue',
        content: 'Làm mới',
        permission: viewPermissions,
        any: Array.isArray(viewPermissions),
        onClick: loadData,
      },
      {
        globalAction: true,
        icon: 'fi fi-rr-file-spreadsheet',
        color: 'green',
        content: loadingExport ? 'Đang xuất' : 'Xuất Excel',
        permission: exportPermissions,
        any: Array.isArray(exportPermissions),
        disabled: loadingExport,
        onClick: handleExportExcel,
      },
      {
        globalAction: true,
        icon: 'fi fi-rr-print',
        color: 'blue',
        content: 'In / Xuất PDF',
        permission: exportPermissions,
        any: Array.isArray(exportPermissions),
        onClick: () => window.print(),
      },
    ],
    [exportPermissions, handleExportExcel, loadData, loadingExport, viewPermissions],
  );

  const summaryItems = useMemo(
    () => getSummaryItems(report.type, dataList.summary, report.code),
    [dataList.summary, report.code, report.type],
  );

  return (
    <FormProvider {...methods}>
      <div className='bw_main_wrapp bc_accounting_reports'>
        {dataList.meta?.unresolved_event_notice ? <p role='alert'>{dataList.meta.unresolved_event_notice}</p> : null}
        {dataList.meta?.unresolved_import_notice ? <p role='alert'>{dataList.meta.unresolved_import_notice}</p> : null}
        <div className='bc_ar_head'>
          <div>
            <span className='bc_ar_eyebrow'>Báo cáo kế toán ERP</span>
            <h2>{report.name}</h2>
          </div>
          <div className='bc_ar_code'>{report.code}</div>
        </div>

        {isBankReport ? <BankReportFilters reportCode={report.code} onSubmit={onSubmitFilter} onClear={onClear} /> : <FilterSearchBar
          title='Tìm kiếm'
          expanded={false}
          colSize={3}
          onSubmit={onSubmitFilter}
          onClear={onClear}
          actions={[
            {
              title: 'Từ khóa',
              component: <FormInput field='keyword' placeholder='Số chứng từ, diễn giải, đối tượng' />,
            },
            {
              title: ['SALE-001', 'SALE-002', 'SALE-005', 'SALE-006'].includes(report.code) ? 'Thời gian ghi nhận' : 'Thời gian hạch toán',
              component: (
                <FormDateRange
                  allowClear
                  fieldStart='accounting_date_from'
                  fieldEnd='accounting_date_to'
                  placeholder={['Từ ngày', 'Đến ngày']}
                  format='DD/MM/YYYY'
                />
              ),
            },
            {
              title: 'Tài khoản',
              component: <FormInput field='accounting_account_code' placeholder='VD: 111, 112, 331' />,
            },
            {
              title: 'Công ty',
              component: <FormSelect field='company_id' list={companyOptions} />,
            },
            {
              title: 'Chi nhánh',
              component: <FormSelect field='business_id' list={businessOptions} />,
            },
            {
              title: 'Cửa hàng',
              component: <FormSelect field='store_id' list={storeOptions} />,
            },
            {
              title: 'Số chứng từ',
              component: <FormInput field='document_no' placeholder='Nhập số chứng từ' />,
              hidden: report.type === REPORT_TYPE.TRIAL_BALANCE,
            },
            {
              title: 'ID tài khoản ngân hàng',
              component: <FormInput field='bank_account_id' placeholder='Lọc theo bank account id' />,
              hidden: !report.code.startsWith('BANK-'),
            },
          ]}
        />}

        {dataList.meta?.bank_report_notice ? <p role='status'>{dataList.meta.bank_report_notice}</p> : null}

        <div className='bc_ar_summary'>
          {summaryItems.map(([label, value, isMoney = true]) => (
            <div className='bc_ar_metric' key={label}>
              <span>{label}</span>
              <strong>{loading ? 'Đang tải…' : isBankReport && value == null ? '—' : isMoney ? formatAmount(value) : formatReportNumber(value)}</strong>
            </div>
          ))}
        </div>

        <DataTable
          noSelect
          columnPreferenceKey={`accounting-report-${report.code.toLowerCase()}`}
          loading={loading}
          title={
            <div className='bc_ar_table_title'>
              <strong>{report.name}</strong>
              <span>{dataList.totalItems} dòng</span>
            </div>
          }
          columns={columns}
          data={dataList.items}
          actions={actions}
          totalPages={dataList.totalPages}
          itemsPerPage={dataList.itemsPerPage}
          page={dataList.page}
          totalItems={dataList.totalItems}
          onChangePage={(page) => setParams((currentParams) => ({ ...currentParams, page }))}
        />
      </div>
    </FormProvider>
  );
};

const AccountingReportPage = () => {
  const { pathname, search } = useLocation();
  const { reportCode } = useParams();
  const report = useMemo(() => {
    if (reportCode) return getAccountingReportByCode(reportCode);
    return getAccountingReportByPath(pathname);
  }, [pathname, reportCode]);

  if (report.code === 'GL-005') {
    return <TrialBalanceReport report={report} />;
  }

  return <GenericAccountingReportPage key={`${report.code}:${search}`} report={report} initialSearch={search} />;
};

export default AccountingReportPage;
